'use client';

import classNames from 'classnames';

import { PropsWithClassname } from '@/app/types';
import { useTranslation } from 'react-i18next';

import { Donation, DonationsListResponse, GetDonationsRequest, GetDonationsStatusesEnum } from '@/app/api';
import useLazyLoad from '@/app/hooks/useLazyLoad';
import { useToast } from '@/app/hooks/useToast';
import { donationsApiForClient } from '@/app/utils/apiForClient';

import Button, { ButtonHTMLTag } from '@/app/components/Button/Button';
import DonationList from '@/app/components/DonationList/DonationList';
import Loader from '@/app/components/Loader/Loader';

interface ProjectDonorsTabProps extends PropsWithClassname {
  subProjectId: string;
}

const SubProjectsDonorsTabs = ({ subProjectId, className }: ProjectDonorsTabProps) => {
  const { t } = useTranslation();
  const { showGenericErrorToast } = useToast();

  const {
    isLoading,
    isLoadMoreAvailable,
    onLoadMore,
    listData: donations,
  } = useLazyLoad<DonationsListResponse, Donation, GetDonationsRequest>({
    getItems: async (filter) => donationsApiForClient.getDonations(filter),
    listDataKey: 'donations',
    filter: {
      subproject: subProjectId,
      statuses: [GetDonationsStatusesEnum.Paid],
    },
    onError: showGenericErrorToast,
  });

  if (!donations.length && !isLoading) {
    return <div className="my-[20px]">{t('components.ProjectDonorsTab.noDonationsYet')}</div>;
  }

  return (
    <div className={classNames(className, 'flex flex-col items-center')}>
      <DonationList className="w-full" donations={donations} />
      {isLoading && <Loader />}
      {isLoadMoreAvailable && (
        <Button
          className={'self-center px-[24px] py-[20px]'}
          tagSpecificProps={{
            htmlTag: ButtonHTMLTag.BUTTON,
            onClick: onLoadMore,
          }}
        >
          {t('components.ProjectDonorsTab.moreDonations')}
        </Button>
      )}
    </div>
  );
};

export default SubProjectsDonorsTabs;
